import { useEffect, useState } from "react";
import useSWR from "swr";
import TargetCardOccupancy from "./TargetCardOccupancy";
import DoughnutChart from "../../../components/charts/DoughnutChart";
import GradientStackedAreaChart from "../../../components/charts/GradientStackedAreaChart";
import { StackedBarNormalization } from "../../../components/charts/StackedBarNormalization";
import { P } from "../../../components/Common/Typo/typo";
import useMonthlyCarbonFootprint from "../../../hooks/useMonthlyCarbonFootprint";
import useOccupancy from "../../../hooks/useOccupancy";
import internalPersonnel from "../../../assets/occupancy/internal-personnel.png";
import externalPersonnel from "../../../assets/occupancy/external-personnel.png";
import defaultPersonnel from "../../../assets/occupancy/default-personnel.png";
import ThaihealthBuilding from "../../../assets/thaihealthbuilding.png";

const barColor: Record<string, string> = {
  staff_percentage: "#E4883E",
  visitor_percentage: "#7BBD60 ",
  carbon_total: "#F5F5F5",
};

const carbonFootprintRename = {
  staff_percentage: "บุคคลากรภายใน",
  visitor_percentage: "บุคคลากรภายนอก",
  carbon_total: "ค่าคาร์บอนฟุตพริ้นท์รวมทั้งอาคาร",
};
const Occupancy = () => {
  const [peopleCountingTodayData, setPeopleCountingTodayData] = useState<any>(
    []
  );
  const [latestTimestamp, setLatestTimestamp] = useState<any>([]);

  const { data: monthlyCarbonData } = useMonthlyCarbonFootprint(12, true);
  const { data: occupancyHookData } = useOccupancy(7, "hourly");
  const { data: peopleCountingTodayHookData } = useSWR([
    "/v1/thaihealth/graph_data/today_people_counting/",
  ]);
  const { data: todayCarbonFootprintData } = useSWR([
    "/v1/thaihealth/graph_data/today_carbon_footprint/",
  ]);
  const { data: occupancyInsightsData } = useSWR([
    "/v1/thaihealth/occupancy_insights/",
  ]);

  useEffect(() => {
    if (peopleCountingTodayHookData?.data?.length) {
      const occupancyPercentages = peopleCountingTodayHookData.data.map(
        (item: any) => {
          const total = item.total_people_count || 1; // กัน division by zero
          const staffPercent = (item.staff_count / total) * 100;
          const visitorPercent = (item.visitor_count / total) * 100;

          return {
            timestamp: item.timestamp,
            staffPercent: parseFloat(staffPercent.toFixed(2)),
            visitorPercent: parseFloat(visitorPercent.toFixed(2)),
            staffCount: item.staff_count,
            visitorCount: item.visitor_count,
            totalPeopleCount: item.total_people_count,
          };
        }
      );

      setLatestTimestamp(occupancyPercentages[occupancyPercentages.length - 1]);
    }
  }, [peopleCountingTodayHookData]);

  useEffect(() => {
    setPeopleCountingTodayData(peopleCountingTodayHookData);
  }, [peopleCountingTodayHookData]);

  const totalImages = 10;
  const personnelPercent = latestTimestamp.staffPercent;
  const internalCount = Math.round((personnelPercent / 100) * totalImages);
  const defaultPersonnelCount = totalImages - internalCount;

  const externalPercent = latestTimestamp.visitorPercent;
  const externalCount = Math.round((externalPercent / 100) * totalImages);
  const defaultExternalCount = totalImages - externalCount;

  const staffPersonnelImages = [
    ...Array.from({ length: internalCount }, () => internalPersonnel),
    ...Array.from({ length: defaultPersonnelCount }, () => defaultPersonnel),
  ];

  const visitorPersonnelImages = [
    ...Array.from({ length: externalCount }, () => externalPersonnel),
    ...Array.from({ length: defaultExternalCount }, () => defaultPersonnel),
  ];

  return (
    <div className="relative h-full w-full overflow-hidden bg-[linear-gradient(135deg,_#fecdd3_0%,_#a5f3fc_30%,_#d9f99d_60%,_#fed7aa_100%)]">
      <div className="absolute top-0 left-0 px-8 py-4 w-full h-full">
        <div className="grid grid-cols-12 gap-5 h-full">
          <div className="col-span-3 h-full">
            <TargetCardOccupancy
              todayCarbonFootprintData={todayCarbonFootprintData}
              occupancyInsightsData={occupancyInsightsData}
            />
          </div>
          <div className="col-span-6 h-full">
            <div className="flex flex-col gap-4 h-full ">
              <div className="flex flex-col gap-2 pb-5 pt-2 px-5 h-[calc(60%-16px)] rounded-[20px] border border-[#EDEFF9] bg-white relative">
                <div className="flex flex-col h-full">
                  <P fontSize="30px" fontWeight="700" color="#1D1D1C">
                   ภาพรวมการปล่อยคาร์บอนจากผู้ใช้งานอาคาร
                  </P>
                  <div className="flex-1 overflow-hidden flex items-center justify-center">
                    <img
                      src={ThaihealthBuilding}
                      alt="ThaihealthBuilding"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </div>
              </div>
              <div className="h-[40%] border border-[#EDEFF9] bg-white rounded-[20px] p-[10px]">
                <div className="flex items-center justify-between">
                  <P fontSize="30px" fontWeight="700" color="#1D1D1C">
                    อัตราการปล่อยคาร์บอนฟุตพริ้นท์ของอาคาร
                    ตามสัดส่วนเวลาการอยู่ของผู้ใช้งาน
                  </P>
                  <div className="px-[8px] rounded-md border border-[#DADADA] bg-[#EFEFEF]">
                    <P fontSize="20px" fontWeight="700" color="#6F6F6E">
                      หน่วย: kgCO₂e
                    </P>
                  </div>
                </div>
                {monthlyCarbonData && (
                  <StackedBarNormalization
                    // map data to data with color
                    data={Object.keys(monthlyCarbonData)
                      .filter(
                        (key) =>
                          carbonFootprintRename[
                            key as keyof typeof carbonFootprintRename
                          ]
                      )
                      .reduce((acc, key) => {
                        const displayName =
                          carbonFootprintRename[
                            key as keyof typeof carbonFootprintRename
                          ];
                        return {
                          ...acc,
                          [displayName]: {
                            ...monthlyCarbonData[
                              key as keyof typeof monthlyCarbonData
                            ],
                            color: barColor[key],
                          },
                        };
                      }, {})}
                    title=""
                    yAxisName=""
                    height="90%"
                    baseSeriesConfig={{
                      name: "ค่าคาร์บอนฟุตพริ้นท์รวมทั้งอาคาร",
                      color: "#808080",
                    }}
                    overlaySeriesNames={["บุคคลากรภายใน", "บุคคลากรภายนอก"]}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="col-span-3">
            <div className="flex flex-col gap-4 h-full relative">
              <div className="flex flex-col gap-2 pb-5 pt-2 px-5 h-[calc(60%-16px)] rounded-[20px] border border-[#EDEFF9] bg-white relative">
                <p className="text-3xl font-bold">
                  จำนวนผู้ใช้งานอาคาร ณ ปัจจุบัน
                </p>
                <div className=" w-full h-full flex flex-col justify-center gap-3">
                  <div className="flex justify-center items-center w-full h-[40%]">
                    <DoughnutChart
                      data={latestTimestamp}
                      color={["#7BBD60", "#F5A623"]}
                      centerLabel={{ title: "รวม", unit: "คน" }}
                      seriesConfig={[
                        { name: "บุคคลภายนอก", dataField: "visitorCount" },
                        { name: "บุคลากรภายใน", dataField: "staffCount" },
                      ]}
                      seriesName="ผู้ใช้งานอาคาร"
                      radius={["69%", "84%"]}
                    />
                  </div>
                  <div className="flex flex-col w-full h-[30%]">
                    <div className="bg-white p-3 h-full rounded-xl flex flex-col gap-2">
                      <div className="flex items-center gap-3 flex-1 bg-[#F9E4B7] border-2 border-[#E6C200] text-center px-2 rounded-lg">
                        <div className="w-[15px] h-[15px] bg-[#FFA500] rounded-[5px]"></div>
                        <span className="text-2xl font-semibold flex justify-between w-full">
                          <span> {"บุคลากรภายใน "}</span>
                          <span>
                            <span className="text-[#FFA500] text-2xl">
                              {latestTimestamp.staffCount}
                            </span>
                            {" คน"}
                          </span>
                        </span>
                      </div>
                      <div className="w-full h-full flex justify-center overflow-hidden">
                        <div className="grid grid-cols-5 sm:grid-cols-10 gap-4 w-full px-8 overflow-hidden">
                          {staffPersonnelImages.map((img, index) => (
                            <div
                              key={index}
                              className="flex justify-center items-center"
                            >
                              <img
                                src={img}
                                alt={`personnel-${index + 1}`}
                                className="w-full h-auto max-w-[12px] sm:max-w-[40px] object-contain"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col w-full h-[30%]">
                    <div className="bg-white p-3 h-full rounded-xl flex flex-col gap-2">
                      <div className="flex items-center gap-3 flex-1 bg-[#7BBD6033] border-2 border-[#7BBD6066] text-center px-2 rounded-lg ">
                        <div className="w-[15px] h-[15px] bg-[#7BBD60] rounded-[5px]"></div>
                        <span className="text-2xl font-semibold flex justify-between w-full ">
                          <span> {"บุคคลภายนอก "}</span>
                          <span>
                            <span className="text-[#7BBD60] text-2xl">
                              {latestTimestamp.visitorCount}
                            </span>
                            {" คน"}
                          </span>
                        </span>
                      </div>
                      <div className="w-full h-full flex justify-center overflow-hidden">
                        <div className="grid grid-cols-5 sm:grid-cols-10 gap-4 w-full px-8 overflow-hidden">
                          {visitorPersonnelImages.map((img, index) => (
                            <div
                              key={index}
                              className="flex justify-center items-center"
                            >
                              <img
                                src={img}
                                alt={`personnel-${index + 1}`}
                                className="w-full h-auto max-w-[12px] sm:max-w-[40px] object-contain"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-[10px] h-[40%] border border-[#EDEFF9] bg-white rounded-[20px] flex flex-col gap-2 px-3 py-3 relative">
                <div className="h-full">
                  <p className="text-3xl font-bold">
                    จำนวนผู้ใช้อาคารในแต่ละช่วงเวลา
                  </p>
                  <div className=" w-full h-full flex flex-col justify-center">
                    <div className="flex justify-center items-center w-full  h-[90%]">
                      {occupancyHookData && (
                        <GradientStackedAreaChart
                          data={peopleCountingTodayData}
                          title=""
                          yAxisName=""
                          height="350px"
                          target={null}
                          seriesConfig={[
                            {
                              name: "บุคคลากรภายใน",
                              color: "#E4883E",
                              dataField: "staff_count",
                            },
                            {
                              name: "บุคคลภายนอก",
                              color: "#6DAF52",
                              dataField: "visitor_count",
                            },
                            {
                              name: "ผู้ใช้งานอาคารทั้งหมด",
                              color: "#00A4C3",
                              dataField: "total_people_count",
                            },
                          ]}
                          unitLabel="คน"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Occupancy;
